import { db, auth } from "@/firebase";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { setDoc } from "firebase/firestore";

function submissionsRef(uid) {
  return collection(db, "users", uid, "essaySubmissions");
}

function submissionDocRef(uid, submissionId) {
  return doc(db, "users", uid, "essaySubmissions", submissionId);
}

function draftDocRef(uid, promptId) {
  return doc(db, "users", uid, "essaySubmissions", `draft_${promptId}`);
}

/**
 * Upsert an autosaved draft document inside essaySubmissions.
 * Uses a stable ID (draft_<promptId>) so repeated saves overwrite the same doc.
 */
export async function upsertDraftSubmission(promptId, essayText, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  const ref = draftDocRef(user.uid, promptId);
  const snap = await getDoc(ref);
  const isNew = !snap.exists();

  await setDoc(
    ref,
    {
      promptId: String(promptId),
      essayText: essayText || "",
      moduleVersion: options.moduleVersion || null,
      status: "draft",
      feedback: null,
      score: null,
      ...(isNew && { createdAt: serverTimestamp() }),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

/**
 * Load an autosaved draft from essaySubmissions (status: "draft").
 * @returns {Promise<{essayText: string} | null>}
 */
export async function loadDraftSubmission(promptId) {
  const user = auth.currentUser;
  if (!user) return null;

  const snap = await getDoc(draftDocRef(user.uid, promptId));
  if (!snap.exists()) return null;
  const data = snap.data();
  // Only return if it's still a draft (not yet promoted to pending/complete)
  if (data.status !== "draft") return null;
  return data;
}

/**
 * Create a new essay submission document with status "pending".
 * Called BEFORE the feedback API request.
 * @returns {Promise<string>} The new submission document ID.
 */
export async function createEssaySubmission({ promptId, promptText, essayText, moduleVersion }) {
  const user = auth.currentUser;
  if (!user) throw new Error("User must be authenticated to save a submission.");

  // If a draft doc exists for this promptId, promote it to pending (same doc).
  // Otherwise create a new addDoc submission.
  if (promptId) {
    const draftRef = draftDocRef(user.uid, String(promptId));
    const draftSnap = await getDoc(draftRef);
    if (draftSnap.exists() && draftSnap.data().status === "draft") {
      await setDoc(draftRef, {
        promptText: promptText || null,
        moduleVersion: moduleVersion || null,
        status: "pending",
        feedback: null,
        score: null,
        updatedAt: serverTimestamp(),
        completedAt: null,
        // Preserve essayText / promptId / createdAt already on the doc
      }, { merge: true });
      return draftRef.id;
    }
  }

  const docRef = await addDoc(submissionsRef(user.uid), {
    promptId: promptId || null,
    promptText: promptText || null,
    essayText: essayText || null,
    moduleVersion: moduleVersion || null,
    status: "pending",
    feedback: null,
    score: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    completedAt: null
  });
  return docRef.id;
}

/**
 * Mark a submission as complete with the returned feedback.
 */
export async function completeEssaySubmission(submissionId, { feedback, score }) {
  const user = auth.currentUser;
  if (!user) return;

  await updateDoc(submissionDocRef(user.uid, submissionId), {
    status: "complete",
    feedback: feedback || null,
    score: score ?? null,
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

/**
 * Mark a submission as errored.
 */
export async function failEssaySubmission(submissionId, errorMessage) {
  const user = auth.currentUser;
  if (!user) return;

  await updateDoc(submissionDocRef(user.uid, submissionId), {
    status: "error",
    errorMessage: errorMessage || "Unknown error",
    updatedAt: serverTimestamp()
  });
}

/**
 * Fetch a single submission document once.
 * @returns {Promise<Object|null>}
 */
export async function getEssaySubmission(submissionId) {
  const user = auth.currentUser;
  if (!user) throw new Error("User must be authenticated.");

  const snap = await getDoc(submissionDocRef(user.uid, submissionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Fetch all submissions for the current user, ordered by createdAt DESC.
 * @returns {Promise<Object[]>}
 */
export async function listEssaySubmissions() {
  const user = auth.currentUser;
  if (!user) throw new Error("User must be authenticated.");

  const q = query(submissionsRef(user.uid), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to real-time updates for the current user's submissions.
 * @param {Function} onData - called with Array of submission objects
 * @param {Function} onError - called on error
 * @returns {Function} unsubscribe function
 */
export function subscribeToEssaySubmissions(onData, onError) {
  const user = auth.currentUser;
  if (!user) {
    if (onError) onError(new Error("User must be authenticated."));
    return () => {};
  }

  const q = query(submissionsRef(user.uid), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    snap => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onData(items);
    },
    err => {
      if (onError) onError(err);
    }
  );
}

/**
 * Subscribe to real-time updates for a single submission.
 * @param {string} submissionId
 * @param {Function} onData - called with the submission object (or null if deleted)
 * @param {Function} onError
 * @returns {Function} unsubscribe function
 */
export function subscribeToEssaySubmission(submissionId, onData, onError) {
  const user = auth.currentUser;
  if (!user) {
    if (onError) onError(new Error("User must be authenticated."));
    return () => {};
  }

  return onSnapshot(
    submissionDocRef(user.uid, submissionId),
    snap => {
      if (!snap.exists()) {
        onData(null);
      } else {
        onData({ id: snap.id, ...snap.data() });
      }
    },
    err => {
      if (onError) onError(err);
    }
  );
}
